import type { BaseUserInfo, External } from "./common";
import type { BaseDocument, Ref } from "./data";
import type { PlausibilityMode } from "./plausibility";
import type { FilterMode } from "./subscription";

export enum SettingsKey {
	Other = "Other",
	PrimaryLocation = "PrimaryLocation",
}

export enum UserStatus {
	Active = "active",
	Deactivated = "deactivated",
	Deleted = "deleted",
	Invited = "invited",
}

export type AllowedSettingTypes = boolean | null | number | string;

export interface Me {
	userId: string;
}

export interface Tenant extends BaseDocument {
	clusterUri?: string; // Can differ per tenant (enterprise isolation)
	dbName: string;
	external: External;
	/** External installation IDs that belong to this tenant */
	installations: string[];
	name: string;
	settings?: TenantSettings;
	tlsSecretId?: string; // AWS Secrets Manager secret name
}

export interface TenantConfig {
	clusterUri?: string;
	dbName: string;
	pemPath?: string;
	settings?: TenantSettings;
	tenantId: string;
}

export interface TenantSettings {
	/**
	 * Minimum severity threshold for counting alerts in the dashboard.
	 * Events with severity >= this threshold are counted.
	 * Range: 1-10. Default: 5.
	 */
	alertSeverityThreshold?: number;
	/**
	 * Whether each alert's description and the camera's rules are checked
	 * against its footage (the footage check, Stage 1b). Independent of
	 * `plausibilityMode`: either one runs the verifier, which runs the stages
	 * each turns on. The check's corrections and rule floors apply to the
	 * measurement in any mode. Off: camera rules are not shown to the first
	 * pass either. Staff-only knob, set through the Shadow Realm. Default:
	 * false.
	 */
	claimsCheckEnabled?: boolean;
	defaultFilterMode: FilterMode;
	/**
	 * Plausibility verification rollout stage for this tenant. `off` means the
	 * verifier ignores the tenant entirely; `shadow` runs the full chain and
	 * records what it would have done without touching measurements, events, or
	 * notifications; `live` rewrites measurement severity. Staff-only knob (set
	 * through the Shadow Realm, never the customer settings endpoint).
	 * Default: `off`.
	 */
	plausibilityMode?: PlausibilityMode;
	/**
	 * Whether the scheduled sensor-health check is allowed to transition this
	 * tenant's sensors between active/warning/offline. When false, sensors are
	 * forced back to active on the next run. Default: true.
	 */
	sensorHealthCheckEnabled?: boolean;
}

export interface TenantUser extends BaseDocument {
	notifications: TenantUserNotifications;
	tenant: Ref<Tenant>;
	user: Ref<User>;
}

export interface TenantUserNotifications {
	email: boolean;
	sms: boolean;
}

export interface User extends BaseDocument, BaseUserInfo {
	external: External;
	isSuperUser: boolean;
}
